import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import * as React from 'react';
import { TouchableWithoutFeedback } from 'react-native';

import type * as api from '@gitbook/api-client';
import {
    type TableCardsView,
    type TableColumnDefinition,
    type TableColumnType,
    type TableDefinitions,
    type TableDiff,
    type TableRecordValue,
    table,
} from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import {
    BaseCard,
    createPalette,
    dropAfter,
    dropBefore,
    dropVertical,
    flex,
    Interactive,
    PalettePositionWidth,
    useConfigurationTheme,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { PlaceholderButton, toast } from '@gitbook/spine-react';
import { useLiveRef } from '@gitbook/util-react-hooks';
import { assert, assertNever } from '@gitbook/util-ts';

import {
    createInputContentRefPalette,
    useContentReference,
    useContentReferenceContext,
} from '../../../../ContentReferences';
import {
    type DNDItemTableRecord,
    getDragItem,
    useDroppableWithCallback,
} from '../../../../DragAndDrop';
import { useInsertFiles } from '../../../../InsertFilesDialog';
import { searchOrganizationMembers } from '../../../../Search';
import { getRealTimeColor, UserAvatar } from '../../../../Users';
import { CardFieldOptions } from '../../../cards';
import { VoidSelectionFrame } from '../../../common';
import {
    createColumnDefsPalette,
    RecordValue,
    type TableDefinitionColorKinds,
    useComputeTableDefinitionColorKinds,
} from '../../ColumnDefinitions';
import { createSelectOptionsPalette } from '../../ColumnDefinitions/select';
import type { TableRecordKV } from '../../types';
import cssStyles from './Cards.module.css';
import { TableRecordControlButton } from './TableRecordControlButton';
import { TableRecordCover } from './TableRecordCover';

/**
 * Card to render a table record.
 */
export function TableRecordCard(props: {
    /** Key of the parent Table block */
    nodeKey: string;
    /** Editor instance */
    editor: EditorContainer;
    /** Is this row editable ? */
    readOnly: boolean;
    /** Record to render in this row */
    record: TableRecordKV;
    /** Definitions in the table */
    definition: TableDefinitions;
    /** View configured in the table */
    view: TableCardsView;
    /** State of the selection for the card */
    selected: boolean;
    selectedBy?: string;
    /** Callback to expand the record as a modal */
    onExpand: () => void;
    /** Is the card the last one in it's row ? */
    isLastInRow: boolean;
    /** Order indexes to position insertion */
    beforeOrderIndex?: string;
    afterOrderIndex?: string;
    /** Computed diff of the table block */
    diff?: TableDiff;
}): React.ReactElement {
    const {
        nodeKey,
        definition,
        editor,
        readOnly,
        record,
        view,
        selected,
        selectedBy,
        isLastInRow: isLast,
        beforeOrderIndex,
        afterOrderIndex,
        onExpand,
        diff,
    } = props;

    const insertFiles = useInsertFiles();
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef);
    const definitionColorKinds = useComputeTableDefinitionColorKinds(definition, view.columns);
    const { color: currentTheme } = useConfigurationTheme();
    const selectionColor = getRealTimeColor(selectedBy);
    const [position, setPosition] = React.useState<'before' | 'after' | undefined>();
    const latestPosition = useLiveRef(position);

    const cardDndItem: DNDItemTableRecord = {
        type: 'table-record',
        id: record.key,
        blockKey: nodeKey,
        previewLabel: 'Move card',
    };

    const drag = useDraggable({
        disabled: readOnly,
        ...getDragItem(cardDndItem),
    });

    const drop = useDroppableWithCallback({
        shouldAccept: React.useCallback(
            (item) => {
                return (
                    item.type === 'table-record' &&
                    record.key !== item.id &&
                    nodeKey === item.blockKey
                );
            },
            [record.key]
        ),
        onDrop: (item) => {
            editor.change((change) => {
                table.moveRecord(
                    change,
                    nodeKey,
                    item.id,
                    latestPosition.current === 'before'
                        ? { before: beforeOrderIndex, after: record.value.orderIndex }
                        : { before: record.value.orderIndex, after: afterOrderIndex }
                );
            });
        },
        onHoverMove: ({ xPosition }) => {
            const newPosition = xPosition === 'right' ? 'after' : 'before';
            if (position !== newPosition) {
                setPosition(newPosition);
            }
        },
    });

    // Resolve the content-reference for the card.
    const targetRef = view.targetDefinition
        ? (record.value.values[view.targetDefinition] as api.ContentRef)
        : undefined;
    const target = useContentReference(targetRef);

    const onSelect = () => {
        editor.change((change) => {
            table.moveTableSelection(change, nodeKey, { recordId: record.key });
            change.focus();
        });
    };

    const cover = table.cards.getRecordCover({
        coverDefinition: view.coverDefinition,
        coverDefinitionDark: view.coverDefinitionDark,
        definition,
        record: record.value,
        mode: currentTheme,
    });

    const body =
        view.columns.length > 0 ? (
            <div className={clsx('flex flex-1 flex-col p-4', !readOnly && 'cursor-text')}>
                {view.columns.map((columnId, index) => (
                    <div
                        key={columnId}
                        className={clsx(
                            'flex flex-col',
                            index < view.columns.length - 1 ? 'mb-2' : ''
                        )}
                    >
                        {view.hideColumnTitle ? null : (
                            <div className="text-muted text-ui-base">
                                {definition[columnId].title}
                            </div>
                        )}
                        {readOnly ? (
                            <RecordValue
                                view={view}
                                record={record}
                                selected={false}
                                columnDefinition={definition[columnId]}
                                nodeKey={nodeKey}
                                editor={editor}
                                readOnly={readOnly}
                                definitionColorKind={definitionColorKinds[columnId]}
                                diff={diff}
                            />
                        ) : (
                            <EditableRecord
                                view={view}
                                columnId={columnId}
                                columnDefinition={definition[columnId]}
                                record={record}
                                editor={editor}
                                nodeKey={nodeKey}
                                readOnly={readOnly}
                                definitionColorKind={definitionColorKinds[columnId]}
                                tableDef={definition}
                            />
                        )}
                    </div>
                ))}
            </div>
        ) : null;

    return (
        <VoidSelectionFrame
            className="flex-1"
            readOnly={readOnly}
            selected={selected}
            selectionColor={selectionColor.base}
            onSelect={onSelect}
        >
            <div
                ref={drop.setNodeRef}
                className={clsx(
                    '@container flex h-full flex-1',
                    cssStyles.targetCard,
                    drag.isDragging && 'opacity-50',
                    dropVertical,
                    {
                        [dropBefore]: drop.isOver && (!isLast || (isLast && position === 'before')),
                        [dropAfter]: drop.isOver && isLast && position === 'after',
                    }
                )}
            >
                <BaseCard
                    style={[flex.flex1, flex.column]}
                    {...(targetRef
                        ? {
                              component: Interactive.TouchableOpacity,
                              // When editing, the onPress will not be called as it's swallowed by the
                              // the TouchableWithoutFeedback.
                              // We prevent the browser from hard-navigation to the link.
                              href: readOnly ? target.href : undefined,
                              onPress: (event) => {
                                  event.preventDefault();
                                  target?.open();
                              },
                          }
                        : {
                              component: Interactive.View,
                          })}
                >
                    {cover ? (
                        <TableRecordCover
                            coverRef={cover.coverRef}
                            objectFit={cover.objectFit}
                            alt={cover.alt}
                        />
                    ) : readOnly ? null : (
                        <div className="flex aspect-video px-1 pt-1">
                            <PlaceholderButton
                                label="Add cover image"
                                icon={Icon.Image}
                                size="large"
                                className="sm:!m-0 sm:!p-0 flex-1 @[170px]:flex-row flex-col"
                                onPress={async () => {
                                    await toast.catch(async () => {
                                        const [coverRef] = await insertFiles.prompt({
                                            title: 'Select file',
                                            accept: 'images',
                                            acceptURL: true,
                                        });

                                        if (!coverRef) {
                                            return;
                                        }

                                        editor.change((change) => {
                                            table.cards.setCardCoverForRecord(change, {
                                                tableKey: nodeKey,
                                                recordId: record.key,
                                                coverRef,
                                                mode: 'light',
                                            });
                                        });

                                        toast.success('Cover image added');
                                    }, 'Error while adding cover image');
                                }}
                            />
                        </div>
                    )}

                    {/* Displays the card body */}
                    {body ? (
                        readOnly ? (
                            body
                        ) : (
                            <TouchableWithoutFeedback
                                onPressIn={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                            >
                                {body}
                            </TouchableWithoutFeedback>
                        )
                    ) : null}

                    {/* Displays the "Drag" and "Edit card" buttons */}
                    <TableRecordControlButton
                        readOnly={readOnly}
                        nodeKey={nodeKey}
                        editor={editor}
                        record={record}
                        view={view}
                        visible={selected}
                        onExpand={onExpand}
                        draggableState={drag}
                        definition={definition}
                    />

                    {/* Displays the "Add field" button */}
                    {readOnly ? null : (
                        <PlaceholderButton
                            label="Add field"
                            icon={Icon.Plus}
                            className="max-h-12"
                            ref={buttonRef}
                            onPress={() => {
                                paletteHandle.open(
                                    createColumnDefsPalette((type) => {
                                        const newCol = table.createColumnFromType(type);
                                        editor.change((change) => {
                                            table.insertColumn(change, nodeKey, newCol);
                                        });
                                    })
                                );
                            }}
                        />
                    )}
                </BaseCard>
            </div>
        </VoidSelectionFrame>
    );
}

type FieldTypeWithPlaceholder = Extract<
    TableColumnType,
    'files' | 'users' | 'select' | 'content-ref' | 'image'
>;

function EditableRecord({
    columnId,
    columnDefinition,
    record,
    editor,
    nodeKey,
    readOnly,
    view,
    definitionColorKind,
    tableDef,
}: {
    columnId: string;
    columnDefinition: TableColumnDefinition;
    record: TableRecordKV;
    editor: EditorContainer;
    nodeKey: string;
    readOnly: boolean;
    view: TableCardsView;
    definitionColorKind: TableDefinitionColorKinds | undefined;
    tableDef: TableDefinitions;
}) {
    const ref = React.useRef<HTMLButtonElement>(null);
    const contentReferenceContext = useContentReferenceContext();
    const hiveClient = useHiveClient();

    const insertFiles = useInsertFiles();
    const paletteHandle = usePaletteHandle(ref);

    const value = record.value.values[columnId];

    const isFieldEmpty = ({
        type,
        value,
    }: {
        type: FieldTypeWithPlaceholder;
        value: TableRecordValue;
    }): boolean => {
        if (!value) return true;

        switch (type) {
            case 'files':
            case 'users':
            case 'select':
                assert(Array.isArray(value));
                return !value.length;
            case 'content-ref':
            case 'image':
                return !value;
            default:
                assertNever(type);
        }
    };

    const getPlaceholderProps = (type: FieldTypeWithPlaceholder) => {
        switch (type) {
            case 'files':
                return {
                    label: 'Upload file',
                    icon: Icon.Paperclip,
                    onPress: async () => {
                        const refs = await insertFiles.prompt({
                            title: 'Select file',
                            accept: 'files',
                            acceptURL: false,
                        });

                        editor.change((change) => {
                            table.setRecordValue(
                                change,
                                nodeKey,
                                record.key,
                                columnId,
                                refs.filter((ref) => ref.kind === 'file').map((ref) => ref.file)
                            );
                        });
                    },
                };
            case 'image':
                return {
                    label: 'Select an image',
                    icon: Icon.Image,
                    onPress: async () => {
                        const [ref] = await insertFiles.prompt({
                            title: 'Select an image',
                            accept: 'images',
                            acceptURL: true,
                        });

                        if (!ref) {
                            return;
                        }

                        editor.change((change) => {
                            table.setRecordValue(change, nodeKey, record.key, columnId, ref);
                        });
                    },
                };
            case 'select':
                return {
                    label: 'Select an option',
                    icon: Icon.ColumnTypeSelect,
                    onPress: () => {
                        assert(columnDefinition.type === 'select');
                        paletteHandle.open(
                            createSelectOptionsPalette({
                                definition: columnDefinition,
                                value: value ? (Array.isArray(value) ? value : []) : [],
                                nodeKey,
                                editor,
                                onValueChange: async (newValue) => {
                                    editor.change((change) => {
                                        table.setRecordValue(
                                            change,
                                            nodeKey,
                                            record.key,
                                            columnId,
                                            newValue
                                        );
                                    });
                                },
                            })
                        );
                    },
                };
            case 'content-ref':
                return {
                    label: 'Link content',
                    icon: Icon.Link,
                    onPress: () => {
                        paletteHandle.open(
                            createInputContentRefPalette(
                                (value) => {
                                    editor.change((change) => {
                                        table.setRecordValue(
                                            change,
                                            nodeKey,
                                            record.key,
                                            columnId,
                                            value
                                        );
                                    });
                                },
                                contentReferenceContext,
                                hiveClient,
                                {
                                    accept: [
                                        'page',
                                        'space',
                                        'collection',
                                        'anchor',
                                        'file',
                                        'url',
                                    ],
                                }
                            )
                        );
                    },
                };
            case 'users':
                return {
                    label: 'Mention users',
                    icon: Icon.Users,
                    onPress: () => {
                        paletteHandle.open(
                            createPalette({
                                search: { placeholder: 'Search users…' },
                                position: { width: PalettePositionWidth.SMALL },
                                groups: [
                                    {
                                        key: 'users',
                                        commands: (palette) => {
                                            const members = searchOrganizationMembers({
                                                organizationId:
                                                    contentReferenceContext.organization,
                                                query: palette.query.text,
                                            });

                                            return members.map((member) => ({
                                                key: member.user.id,
                                                title: member.user.displayName,
                                                icon: () => (
                                                    <UserAvatar user={member.user} size="xs" />
                                                ),
                                                trigger: () => {
                                                    editor.change((change) => {
                                                        table.setRecordValue(
                                                            change,
                                                            nodeKey,
                                                            record.key,
                                                            columnId,
                                                            [member.user.id]
                                                        );
                                                    });
                                                },
                                            }));
                                        },
                                    },
                                ],
                            })
                        );
                    },
                };
            default:
                assertNever(type);
        }
    };

    /* For fields that are not checkbox, rating, text or number (these have their own empty-state UI), we render a placeholder button.
     * The button makes it recognizable that a field is empty and requires data entry + reduces the amount of steps needed to insert said data.
     */
    const excludedFieldTypes = ['checkbox', 'rating', 'text', 'number'];

    if (
        !excludedFieldTypes.includes(columnDefinition.type) &&
        isFieldEmpty({
            type: columnDefinition.type as FieldTypeWithPlaceholder,
            value,
        })
    ) {
        return (
            <PlaceholderButton
                ref={ref}
                kind="pill"
                className="w-fit"
                size="small"
                {...getPlaceholderProps(columnDefinition.type as FieldTypeWithPlaceholder)}
            />
        );
    }

    /* Render the field if it has its own handling of empty states (checkbox, rating, text, number) OR when it has data. */
    return (
        <div
            className={clsx(
                'group/record-value relative flex min-h-6 items-center rounded-lg',
                cssStyles.cardFieldBg
            )}
        >
            <RecordValue
                record={record}
                selected={false}
                columnDefinition={columnDefinition}
                nodeKey={nodeKey}
                editor={editor}
                readOnly={readOnly}
                view={view}
                definitionColorKind={definitionColorKind}
            />
            <CardFieldOptions
                nodeKey={nodeKey}
                editor={editor}
                view={view}
                columnDefinition={columnDefinition}
                trailingIcon={Icon.MoreVertical}
                tableDef={tableDef}
                record={record}
            />
        </div>
    );
}
